import React, { useState, useRef, useEffect } from "react";
import "./AskAi.css";

function AskAi() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterStage, setFilterStage] = useState(null);
  const [tokenOptions, setTokenOptions] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const chatEndRef = useRef(null);
  const [dataset, setDataset] = useState("cryptoAnalysis"); // default to active

  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal
    ? "http://localhost:5000"
    : "https://www.trendscout.club";

  const filters = {
    recommendation: ["BUY", "SELL", "STRONG SELL", "STRONG BUY", "HOLD", "SKIP"],
    trend: ["BULLISH", "BEARISH", "SIDEWAYS", "OTHER"],
    status: ["TRIGGERED", "CANCELLED"],
  };

  const sendMessage = async (customInput, overrideTokenId = null) => {
    const inputToSend = customInput || input;
    if (!inputToSend.trim()) return;

    const userMessage = { role: "user", content: inputToSend };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setInput("");

    try {
      const res = await fetch(`${baseUrl}/api/ask-ai-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: [...messages, userMessage], tokenId: overrideTokenId || selectedToken?._id || null, dataset }),
      });
      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Something went wrong." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (type) => {
    setFilterStage(type);
    setTokenOptions([]);
  };

  const handleFilterOption = async (value) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/tokens?${filterStage}=${value}&dataset=${dataset}`
      );

      const tokens = await res.json();
      setTokenOptions(tokens);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `✅ Found ${tokens.length} tokens for ${value}. Select one to explore.`,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "❌ Failed to fetch tokens." },
      ]);
    }
  };

  const checkTokenOutcome = async () => {
    if (!selectedToken?._id) return;

    try {
      const res = await fetch(`${baseUrl}/api/token-analysis/${selectedToken._id}?dataset=${dataset}`);
      const data = await res.json();

      if (data.aiSummary) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "📊 Here's a full trade outcome summary:",
          },
          { role: "assistant", content: data.aiSummary },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "No outcome summary available." },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Failed to analyze token outcome." },
      ]);
    }
  };

  const handleTokenSelect = async (token) => {
    setSelectedToken(token);
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: `📘 Loaded token: ${token.token} from ${new Date(
          token.timestamp
        ).toLocaleDateString()}. Ask anything about it!`,
      },
    ]);
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  return (
    <div className="aichat-container-pages">
      {/* Dashboard area for filters */}
      <div className="filter-dashboard">
<div className="button-group">
  <button
    onClick={() => setDataset("cryptoAnalysis")}
    className={dataset === "cryptoAnalysis" ? "active-db" : ""}
  >
    🟢 Active
  </button>

  <button
    onClick={() => setDataset("aitriggered")}
    className={dataset === "aitriggered" ? "active-db" : ""}
  >
    🚀 Triggered
  </button>

  <button
    onClick={() => setDataset("aiinactive")}
    className={dataset === "aiinactive" ? "active-db" : ""}
  >
    🛑 Inactive
  </button>
</div>


        <div className="button-group">
          <button onClick={() => handleFilterClick("recommendation")}>
            📊 Recommendation
          </button>
          <button onClick={() => handleFilterClick("trend")}>📈 Trend</button>
          <button onClick={() => handleFilterClick("status")}>📌 Status</button>
        </div>
        {filterStage && (
          <div className="filter-options">
            {filters[filterStage].map((f) => (
              <button key={f} onClick={() => handleFilterOption(f)}>
                {f}
              </button>
            ))}
          </div>
        )}
        {tokenOptions.length > 0 && (
          <div className="token-options">
            {tokenOptions.map((t) => (
              <button key={t._id} onClick={() => handleTokenSelect(t)}>
                {t.token} ({new Date(t.timestamp).toLocaleDateString()})
              </button>
            ))}
          </div>
        )}
        {selectedToken && (
          <div className="token-options">
            <button onClick={checkTokenOutcome}>📊 Check Outcome</button>
          </div>
        )}
      </div>

      <div className="ask-ai-container">
        <div className="chat-header">🤖 Ask AI About Tokens</div>

        <div className="chat-window">
          {messages.map((msg, i) => (
            <div key={i} className={`chat-bubble ${msg.role}`}>
              <div className="bubble-content">{msg.content}</div>
            </div>
          ))}
          {loading && (
            <div className="chat-bubble assistant">
              <div className="bubble-content">💭 Thinking...</div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div className="chat-input-bar">
          <input
            type="text"
            placeholder="Ask about tokens..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          />
          <button onClick={() => sendMessage()} disabled={loading}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

export default AskAi;
